import { makeRelId } from "../sqlite/store.js";
import { DOMAIN_RANGE, RELATION_TYPES, domainRangeViolated, typeFromId } from "../enrich/domain-range.js";
import type { ObjectType, OntObject, OntRelation, RelationType } from "../types.js";
import type { ExtractResult, LinkCandidate, RecordInput, RelationDiagnostic } from "./types.js";

const WIKILINK = /\[\[([^\]]+)\]\]/g;

function basenameSlug(path: string): string {
  const base = path.replace(/\\/g, "/").split("/").pop() ?? path;
  return base.replace(/\.md$/i, "");
}

function str(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}

function rel(sourceId: string, r: RelationType, targetId: string, evidence: string): OntRelation {
  return {
    relId: makeRelId(sourceId, r, targetId),
    rel: r,
    sourceId,
    targetId,
    provenance: "deterministic",
    confidence: 1,
    confirmed: null,
    evidence,
  };
}

function wikilinks(sourceId: string, body: string, evidence: string): LinkCandidate[] {
  const out: LinkCandidate[] = [];
  for (const m of body.matchAll(WIKILINK)) {
    const parts = (m[1] ?? "").split("|");
    const targetRef = (parts[0] ?? "").trim();
    if (targetRef) out.push({ sourceId, targetRef, evidence });
  }
  return out;
}

function empty(): ExtractResult {
  return { objects: [], relations: [], links: [] };
}

/**
 * A record's `relations:` frontmatter field → deterministic TYPED relations, the
 * decision-graph counterpart of the failure ledger's `recurrence_key`/`rule`
 * fields. Each entry is a single-key map `{<reltype>: <target>}` (the YAML
 * `- builds-on: decision:<slug>` shape).
 *
 * The store does NOT validate relation type or domain/range on a deterministic
 * upsert (it only blocks unconfirmed-llm), so — unlike the hard-coded
 * `fromFailure` edges — a free-form authored field MUST be validated here. Three
 * ways an entry is rejected (→ a diagnostic, never a silent drop):
 *   1. unknown relation type,
 *   2. target is untyped AND its type can't be inferred (the rel's range is not a
 *      single type) → ambiguous; the author must write a typed id (`type:slug`),
 *   3. the (sourceType, rel, targetType) triple violates domain/range.
 * A well-formed entry whose target object simply doesn't exist is NOT rejected
 * here — it FK-orphans at upsert and the rebuild lists that case separately.
 *
 * Target typing: a bare slug is upgraded to `<rangeType>:<slug>` ONLY when the
 * rel's range is exactly one type (e.g. `applies` → `rule:`); for a multi/any
 * range (`builds-on`, `relates-to`) the author must type it explicitly.
 */
function fromRelationsField(
  record: RecordInput,
  sourceId: string,
  sourceType: ObjectType,
): { relations: OntRelation[]; diagnostics: RelationDiagnostic[] } {
  const raw = record.frontmatter["relations"];
  if (raw === undefined || raw === null) return { relations: [], diagnostics: [] };
  const relations: OntRelation[] = [];
  const diagnostics: RelationDiagnostic[] = [];
  const reject = (rel: string, target: string, reason: string) =>
    diagnostics.push({ source: record.path, rel, target, reason });
  // A non-list `relations:` (e.g. the author wrote a map `relations:\n  applies: rule:x`
  // instead of a list `- applies: rule:x`) is a common YAML shape mistake — surface it,
  // don't silently drop the whole field.
  if (!Array.isArray(raw)) {
    reject("?", "(relations field)", "relations: must be a list of `<relation>: <target>` maps");
    return { relations, diagnostics };
  }

  for (const entry of raw as unknown[]) {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
      reject("?", String(entry), "entry must be a `<relation>: <target>` map");
      continue;
    }
    for (const [relRaw, targetRaw] of Object.entries(entry as Record<string, unknown>)) {
      const relName = relRaw.trim();
      const target = str(targetRaw);
      if (!target) {
        reject(relName, String(targetRaw), "target is empty");
        continue;
      }
      if (!(RELATION_TYPES as readonly string[]).includes(relName)) {
        reject(relName, target, "unknown relation type");
        continue;
      }
      const relType = relName as RelationType;
      // Resolve the target to a typed id. A colon means the author wrote an explicit
      // typed id (`type:slug`): it must have a KNOWN type prefix AND a non-empty slug —
      // a typo'd prefix (`decison:foo`) or an empty slug (`rule:`) is a malformed id, not
      // a bare slug to be re-prefixed. No colon ⇒ a bare slug, typed by inference only
      // when the rel's range is a single type (e.g. `applies` → `rule:`).
      let targetId: string;
      let tgtType: ObjectType | null;
      const colon = target.indexOf(":");
      if (colon >= 0) {
        tgtType = typeFromId(target);
        if (!tgtType || target.slice(colon + 1).trim().length === 0) {
          reject(relName, target, "malformed typed target (expected type:slug)");
          continue;
        }
        targetId = target;
      } else {
        const range = DOMAIN_RANGE[relType].range;
        if (range.length === 1) {
          tgtType = range[0]!;
          targetId = `${tgtType}:${target}`;
        } else {
          reject(relName, target, "ambiguous target — write a typed id (type:slug)");
          continue;
        }
      }
      if (domainRangeViolated(relType, sourceType, tgtType)) {
        reject(relName, targetId, `domain/range violation (${sourceType} ${relName} ${tgtType})`);
        continue;
      }
      relations.push(rel(sourceId, relType, targetId, record.path));
    }
  }
  return { relations, diagnostics };
}

/** Merge a `relations:` field into a base extraction result (decision/memory/runbook). */
function withRelationsField(base: ExtractResult, record: RecordInput, sourceType: ObjectType): ExtractResult {
  const sourceId = base.objects[0]?.id;
  if (!sourceId) return base;
  const { relations, diagnostics } = fromRelationsField(record, sourceId, sourceType);
  if (relations.length === 0 && diagnostics.length === 0) return base;
  return {
    ...base,
    relations: [...base.relations, ...relations],
    ...(diagnostics.length ? { diagnostics: [...(base.diagnostics ?? []), ...diagnostics] } : {}),
  };
}

/**
 * Failure ledger -> the recurring-failure subgraph (powers the §5A guard,
 * fully deterministic). One canonical `problem:failure:<key>` class object;
 * each occurrence file becomes its own object with a `recurs-as` edge to the
 * class (so the store's recurrence count = number of occurrences). The optional
 * `rule` field becomes a `covered-by` edge to a `rule:<slug>` object.
 */
function fromFailure(record: RecordInput): ExtractResult {
  const key = str(record.frontmatter["recurrence_key"]);
  if (!key) return empty();
  const classId = `problem:failure:${key}`;
  // Occurrence id uses the file basename. The extracted categories (_failures,
  // decision-log, _memory) are FLAT directories, so basenames are unique there;
  // nested categories (worklog) are not extracted. If a category ever nests,
  // switch this to a full-relative-path discriminator.
  const occId = `${classId}@${basenameSlug(record.path)}`;
  // Display label = the record's H1 (the human, usually-Korean "what"), falling back to the
  // English recurrence_key. The id stays the key — labels are display-only, so this just makes
  // the failure nodes readable in the graph / why output without changing identity or relations.
  const what = (record.body.match(/^#\s+(.+)$/m)?.[1] ?? key).trim();
  const objects: OntObject[] = [
    { id: classId, type: "problem", subtype: "failure", label: what, source: null },
    { id: occId, type: "problem", subtype: "failure", label: what, source: record.path },
  ];
  const relations: OntRelation[] = [rel(occId, "recurs-as", classId, record.path)];
  const ruleSlug = str(record.frontmatter["rule"]);
  if (ruleSlug) {
    objects.push({ id: `rule:${ruleSlug}`, type: "rule", label: ruleSlug, source: null });
    relations.push(rel(classId, "covered-by", `rule:${ruleSlug}`, record.path));
  }
  return { objects, relations, links: [] };
}

/**
 * Decision log -> a `decision:<slug>` object (label = the H1 title). Typed
 * relations like `supersedes` are not structured in the records yet, so they
 * surface as wikilink candidates for human typing (§4).
 */
function fromDecision(record: RecordInput): ExtractResult {
  const slug = basenameSlug(record.path);
  const id = `decision:${slug}`;
  const title = (record.body.match(/^#\s+(.+)$/m)?.[1] ?? slug).trim();
  return withRelationsField(
    {
      objects: [{ id, type: "decision", label: title, source: record.path }],
      relations: [],
      links: wikilinks(id, record.body, record.path),
    },
    record,
    "decision",
  );
}

/**
 * Runbook -> an `action:<slug>` object (label = the H1 title). A runbook is an
 * executable/checkable remedy (§1 Action/Guard), so it becomes a `resolved-by`
 * TARGET that LLM enrichment (or a future structured field) can link a failure to.
 * Body wikilinks are candidates for human typing.
 */
function fromRunbook(record: RecordInput): ExtractResult {
  const slug = basenameSlug(record.path);
  if (slug === "_INDEX" || slug.startsWith("_")) return empty();
  const id = `action:${slug}`;
  const title = (record.body.match(/^#\s+(.+)$/m)?.[1] ?? slug).trim();
  return withRelationsField(
    {
      objects: [{ id, type: "action", label: title, source: record.path }],
      relations: [],
      links: wikilinks(id, record.body, record.path),
    },
    record,
    "action",
  );
}

/** Memory -> a `rule:<slug>` object (label = frontmatter `name`); body wikilinks as candidates. */
function fromMemory(record: RecordInput): ExtractResult {
  const slug = basenameSlug(record.path);
  if (slug === "_INDEX" || slug === "MEMORY") return empty();
  const id = `rule:${slug}`;
  const label = str(record.frontmatter["name"]) ?? slug;
  return withRelationsField(
    {
      objects: [{ id, type: "rule", label, source: record.path }],
      relations: [],
      links: wikilinks(id, record.body, record.path),
    },
    record,
    "rule",
  );
}

/**
 * Phase-2 durability: an ontos-owned synthesized record holds CONFIRMED knowledge
 * that has no markdown of its own — chiefly conversation-confirmed objects/relations
 * (and the read-back home for confirmed `ontos:` writeback in general). Reads two
 * frontmatter lists: `ontos_objects` (id/type/label → objects) and `ontos_relations`
 * (relId/rel/source/target/confirmed → CONFIRMED llm relations, which the trusted-graph
 * guard accepts only because they carry a `confirmed` date). This is what lets confirmed
 * conversation knowledge SURVIVE a deterministic rebuild + sync via git.
 */
function fromOntosRecord(record: RecordInput): ExtractResult {
  const objects: OntObject[] = [];
  const relations: OntRelation[] = [];
  const rawObjs = record.frontmatter["ontos_objects"];
  if (Array.isArray(rawObjs)) {
    for (const o of rawObjs as Record<string, unknown>[]) {
      const id = str(o?.id);
      const type = str(o?.type);
      const label = str(o?.label) ?? id;
      if (id && type) objects.push({ id, type: type as ObjectType, subtype: (str(o?.subtype) as OntObject["subtype"]) ?? null, label: label!, source: str(o?.source) ?? record.path });
    }
  }
  const rawRels = record.frontmatter["ontos_relations"];
  if (Array.isArray(rawRels)) {
    for (const r of rawRels as Record<string, unknown>[]) {
      const relId = str(r?.relId);
      const r2 = str(r?.rel);
      const source = str(r?.source);
      const target = str(r?.target);
      const confirmed = str(r?.confirmed);
      // Only CONFIRMED relations belong in a durable record (the guard requires it for llm).
      if (relId && r2 && source && target && confirmed) {
        relations.push({
          relId,
          rel: r2 as RelationType,
          sourceId: source,
          targetId: target,
          provenance: "llm",
          confidence: typeof r?.confidence === "number" ? (r.confidence as number) : 0.5,
          confirmed,
          evidence: str(r?.evidence) ?? record.path,
        });
      }
    }
  }
  return { objects, relations, links: [] };
}

/**
 * Deterministic extraction (Phase 1 §4 step 2) — the fast-proof core, no LLM.
 * Dispatches by record category. Confidently extracts the failure-recurrence +
 * coverage subgraph and the object set; supersedes/causes chains arrive as
 * wikilink candidates until an `ontos:` block or LLM enrichment types them.
 */
/**
 * Read the `ontos:` writeback block (the §5-bis confirm-time mirror) from ANY record's
 * frontmatter into CONFIRMED relations. This block — unlike `ontos_objects`/`ontos_relations`,
 * which live only on ontos-OWNED records — is written into the SOURCE record of a confirmed
 * relation, whatever its category (a decision, a memory, …). So it must be read for every
 * record, not just `category === "ontos"`. Each entry is self-contained (relId/rel/source/
 * target/confirmed) since writeback now persists the source NODE id.
 */
const PROVENANCE_ENUM = new Set(["deterministic", "llm", "wikilink-promoted"]);
function fromOntosBlock(record: RecordInput): OntRelation[] {
  const raw = record.frontmatter["ontos"];
  if (!Array.isArray(raw)) return [];
  const out: OntRelation[] = [];
  const seen = new Set<string>();
  for (const r of raw as Record<string, unknown>[]) {
    if (!r || typeof r !== "object") continue;
    const relId = str(r.relId);
    const rel = str(r.rel);
    const source = str(r.source);
    const target = str(r.target);
    const confirmed = str(r.confirmed);
    if (!relId || !rel || !source || !target || !confirmed) continue; // partial/garbage entry — ignore
    // Skip a LEGACY 0.3.2-shaped entry, where `source` held the PROVENANCE (e.g. "llm") and there
    // was no `provenance` key — treating that as a source NODE id would inject a bogus relation.
    if (!str(r.provenance) && PROVENANCE_ENUM.has(source)) continue;
    if (seen.has(relId)) continue; // dedup within the block
    seen.add(relId);
    out.push({
      relId,
      rel: rel as RelationType,
      sourceId: source,
      targetId: target,
      provenance: "llm",
      confidence: typeof r.confidence === "number" ? (r.confidence as number) : 0.5,
      confirmed,
      evidence: str(r.evidence) ?? record.path,
    });
  }
  return out;
}

export function extractDeterministic(record: RecordInput): ExtractResult {
  const base = ((): ExtractResult => {
    switch (record.category) {
      case "failure":
        return fromFailure(record);
      case "decision":
        return fromDecision(record);
      case "memory":
        return fromMemory(record);
      case "runbook":
        return fromRunbook(record);
      case "ontos":
        return fromOntosRecord(record);
      default:
        return empty();
    }
  })();
  // The `ontos:` writeback block can ride on a record of ANY category, so read it for all of
  // them (this is what lets a confirmed record-sourced relation survive a rebuild). Dedup by
  // relId — the block is authoritative for its own entries.
  const block = fromOntosBlock(record);
  if (block.length === 0) return base;
  const seen = new Set(block.map((r) => r.relId));
  return { ...base, relations: [...base.relations.filter((r) => !seen.has(r.relId)), ...block] };
}
